import React from "react";
import classNames from "classnames";
import Icon from "./Icon";

interface PanelProps {
  title?: React.ReactNode;
  titleClassName?: string;
  titleBorder?: boolean;
  icon?: string;
  iconBadge?: boolean;
  iconColor?: string;
  iconSize?: string;
  iconBadgeBg?: string;
  iconLabel?: React.ReactNode;
  iconLabelClassName?: string;
  bg?: string;
  shadow?: boolean;
  badge?: React.ReactNode;
  footer?: React.ReactNode;
  contentClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const Panel = ({
  title,
  titleClassName,
  titleBorder = true,
  icon,
  iconBadge = false,
  iconColor,
  iconSize,
  iconBadgeBg = "bg-orange-50",
  iconLabel,
  iconLabelClassName = "text-xs font-semibold uppercase tracking-widest text-orange-500",
  bg,
  shadow = true,
  badge,
  footer,
  contentClassName,
  className,
  style,
  children,
}: PanelProps) => {
  const resolvedTitleClassName =
    titleClassName ??
    (iconBadge
      ? "font-semibold text-gray-900"
      : "text-sm font-semibold text-gray-700");

  const baseClassName = classNames(
    bg ?? "bg-white",
    shadow && "shadow-sm",
    "rounded-2xl overflow-hidden",
    className
  );

  if (iconBadge) {
    const resolvedContentClassName =
      contentClassName ?? "p-6 flex flex-col gap-3";
    return (
      <div className={baseClassName} style={style}>
        <div className={resolvedContentClassName}>
          {icon && (
            <div
              className={classNames(
                "flex items-center gap-3",
                !!iconLabel && "mb-4"
              )}
            >
              <div
                className={classNames(
                  "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                  iconBadgeBg
                )}
              >
                <Icon
                  name={icon}
                  color={iconColor ?? "orange"}
                  size={iconSize ?? "large"}
                />
              </div>
              {iconLabel && (
                <span className={iconLabelClassName}>{iconLabel}</span>
              )}
            </div>
          )}
          {title && <h3 className={resolvedTitleClassName}>{title}</h3>}
          {children}
        </div>
        {footer && (
          <div className="px-5 py-3 border-t border-gray-100">{footer}</div>
        )}
      </div>
    );
  }

  return (
    <div className={baseClassName} style={style}>
      {title && (
        <div
          className={classNames(
            "px-5 flex items-center justify-between gap-3",
            titleBorder ? "py-4 border-b border-gray-100" : "pt-4"
          )}
        >
          <h3
            className={classNames(
              resolvedTitleClassName,
              "flex items-center gap-2"
            )}
          >
            {icon && <Icon name={icon} />}
            {title}
          </h3>
          {badge && (
            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
              {badge}
            </span>
          )}
        </div>
      )}

      <div className={classNames("px-5 py-4", contentClassName)}>
        {children}
      </div>

      {footer && (
        <div className="px-5 py-3 border-t border-gray-100">{footer}</div>
      )}
    </div>
  );
};

export default Panel;
